/**
 * PomodoroTimer - Main timer display for the Focus/Pomodoro module.
 *
 * The container component that composes TimerRing (SVG countdown) and
 * TimerControls (start/pause/resume/cancel). Displays the current phase label,
 * cycle count ("Work Session 2/4", "Short Break", "Long Break"), and linked
 * task title if present.
 *
 * Reads all state from usePomodoroStore.
 *
 * @see Requirements 7.1, 7.2, 7.5, 7.7
 */

import { useState } from 'react';
import { usePomodoroStore } from '@/stores';
import { TimerRing } from './TimerRing';
import { TimerControls } from './TimerControls';
import { TaskSelector } from './TaskSelector';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Generate the phase label string for display. */
function getPhaseLabel(
  phase: 'idle' | 'work' | 'shortBreak' | 'longBreak',
  sessionCount: number,
  sessionsBeforeLongBreak: number
): string {
  switch (phase) {
    case 'work':
      return `Work Session ${sessionCount + 1}/${sessionsBeforeLongBreak}`;
    case 'shortBreak':
      return 'Short Break';
    case 'longBreak':
      return 'Long Break';
    case 'idle': {
      if (sessionCount === 0) return 'Ready to Focus';
      return `Session ${sessionCount}/${sessionsBeforeLongBreak} Complete`;
    }
    default:
      return '';
  }
}

// ─── Duration Picker ─────────────────────────────────────────────────────────

/** Quick-select duration presets and custom input, visible only when idle. */
function DurationPicker() {
  const config = usePomodoroStore((s) => s.config);
  const updateConfig = usePomodoroStore((s) => s.updateConfig);
  const phase = usePomodoroStore((s) => s.phase);
  const [custom, setCustom] = useState('');

  if (phase !== 'idle') return null;

  const presets = [5, 10, 15, 25, 30, 45, 60];

  return (
    <div className="flex flex-wrap items-center justify-center gap-2" role="group" aria-label="Work duration presets">
      {presets.map((mins) => (
        <button
          key={mins}
          onClick={() => {
            setCustom('');
            updateConfig({ workDuration: mins });
          }}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all
            ${config.workDuration === mins && !custom
              ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg'
              : 'bg-white/5 text-text-secondary hover:bg-white/10'}`}
          aria-pressed={config.workDuration === mins && !custom}
        >
          {mins}m
        </button>
      ))}
      <input
        type="number"
        min={1}
        max={120}
        value={custom || config.workDuration}
        onChange={(e) => {
          const val = parseInt(e.target.value);
          if (val >= 1 && val <= 120) {
            setCustom(e.target.value);
            updateConfig({ workDuration: val });
          } else {
            setCustom(e.target.value);
          }
        }}
        className="w-16 px-2 py-1.5 rounded-full text-center text-sm bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-pink-500/50"
        aria-label="Custom duration in minutes"
      />
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function PomodoroTimer() {
  const {
    phase,
    remainingSeconds,
    totalSeconds,
    sessionCount,
    linkedTaskTitle,
    config,
  } = usePomodoroStore();

  const phaseLabel = getPhaseLabel(phase, sessionCount, config.sessionsBeforeLongBreak);

  // Determine accent color for the phase badge
  const phaseBadgeClass =
    phase === 'work'
      ? 'bg-primary-light text-primary-dark'
      : phase === 'shortBreak'
        ? 'bg-pomodoro-break/20 text-pomodoro-break-text'
        : phase === 'longBreak'
          ? 'bg-secondary-light text-pomodoro-long-text'
          : 'bg-bg-secondary text-text-secondary';

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-8">
      {/* Phase label */}
      <div className="flex flex-col items-center gap-2">
        <span
          className={`
            inline-flex items-center px-4 py-1.5 rounded-full
            text-sm font-medium
            ${phaseBadgeClass}
          `}
          aria-live="polite"
          aria-atomic="true"
        >
          {phaseLabel}
        </span>

        {/* Linked task title */}
        {linkedTaskTitle && (
          <p className="text-sm text-text-secondary max-w-[280px] text-center truncate">
            Focused on: <span className="font-medium text-text-primary">{linkedTaskTitle}</span>
          </p>
        )}
      </div>

      {/* Duration Picker — only visible when idle */}
      <DurationPicker />

      {/* Timer Ring */}
      <TimerRing
        remainingSeconds={remainingSeconds}
        totalSeconds={totalSeconds}
        phase={phase}
      />

      {/* Cycle progress dots */}
      {(phase !== 'idle' || sessionCount > 0) && (
        <div
          className="flex items-center gap-2"
          aria-label={`Cycle progress: ${sessionCount} of ${config.sessionsBeforeLongBreak} sessions completed`}
          role="status"
        >
          {Array.from({ length: config.sessionsBeforeLongBreak }).map((_, i) => (
            <span
              key={i}
              className={`
                w-2.5 h-2.5 rounded-full transition-colors duration-fast
                ${i < sessionCount ? 'bg-primary' : 'bg-border'}
              `}
              aria-hidden="true"
            />
          ))}
        </div>
      )}

      {/* Timer Controls */}
      <TimerControls />

      {/* Task Selector — only visible when idle */}
      {phase === 'idle' && (
        <div className="w-full max-w-[280px]">
          <TaskSelector />
        </div>
      )}
    </div>
  );
}

PomodoroTimer.displayName = 'PomodoroTimer';
